import {
  Box,
  Drawer,
  DrawerProps as MuiDrawerProps,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useGesture } from "@use-gesture/react";
import { PropsWithChildren } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useLocation } from "react-router-dom";

import { useNavigationStore } from "@/components/nav/state";
import { useOnValueChanged } from "@/hooks";

import {
  PAGE_CONTENT_MARGIN,
  PRIMARY_NAVIGATION_COLLAPSE_WIDTH,
  PRIMARY_NAVIGATION_HEADER_ROW_HEIGHT,
} from "../../constants";
import { PrimaryNavigationContainer } from "./components/PrimaryNavigationContent/components/PrimaryNavigationContainer/PrimaryNavigationContainer";

interface DrawerProps extends MuiDrawerProps {
  width: number;
  // Pushes the fixed-position Drawer down (px), e.g. below an app banner.
  topOffset: number;
}
const extraProps: PropertyKey[] = ["width", "topOffset"];

const DRAWER_TOP_MARGIN =
  PRIMARY_NAVIGATION_HEADER_ROW_HEIGHT + PAGE_CONTENT_MARGIN;
const DRAWER_TOGGLE_BUTTON_MARGIN = 120;

const PrimaryNavigationDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => !extraProps.includes(prop),
})<DrawerProps>(({ theme, variant, width, topOffset }) => ({
  width,
  ...(variant === "temporary" ? { top: DRAWER_TOP_MARGIN + topOffset } : {}),

  "& .MuiBackdrop-root": {
    top: DRAWER_TOP_MARGIN + topOffset,
    "&::before": {
      content: '""',
      position: "absolute",
      top: -DRAWER_TOP_MARGIN,
      left: DRAWER_TOGGLE_BUTTON_MARGIN,
      height: DRAWER_TOP_MARGIN,
      width: "100%",
      backgroundColor: "rgba(0,0,0,0.5)",
    },
    "&::after": {
      // this covers the top left corner
      pointerEvents: "none",
      content: '""',
      position: "absolute",
      top: -DRAWER_TOP_MARGIN,
      left: 0,
      height: DRAWER_TOP_MARGIN,
      width: DRAWER_TOGGLE_BUTTON_MARGIN,
      backgroundColor: "rgba(0,0,0,0.5)",
    },
  },

  "& .MuiDrawer-paper": {
    width,
    overflowX: "hidden",

    ...(variant === "temporary"
      ? {
          height: "auto",
          top: DRAWER_TOP_MARGIN + topOffset,
          left: PAGE_CONTENT_MARGIN,
          bottom: PAGE_CONTENT_MARGIN,
          border: `1px solid ${theme.palette.secondary[6]}`,
          borderRadius: "4px",
        }
      : {
          border: "none",
          borderRadius: 0,
          top: topOffset,
          height: `calc(100% - ${topOffset}px)`,
        }),
  },
}));

const ResizeTrack = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "10px",
  bottom: "10px",
  right: "-4px",
  width: "7px",

  cursor: "col-resize",
  "&:hover": {
    backgroundColor: theme.palette.secondary[4],
  },
}));

interface PrimaryNavigationProps extends PropsWithChildren {
  /** Pushes the fixed-position Drawer down (px), e.g. below an app banner. */
  topOffset?: number;
}

// TODO: Refactor PrimaryNavigation, its structure is really not optimal!!!
export const PrimaryNavigation = (props: PrimaryNavigationProps) => {
  const { children, topOffset = 0 } = props;
  const {
    primaryNavigationWidth,
    setPrimaryNavigationWidth,
    primaryNavigationVariant,
    setPrimaryNavigationVariant,
    isPrimaryNavigationExpanded,
    collapsePrimaryNavigation,
  } = useNavigationStore();

  const { pathname } = useLocation();

  useOnValueChanged(pathname, () => {
    if (isPrimaryNavigationExpanded) {
      collapsePrimaryNavigation();
    }
  });

  const isDrawerOpen =
    primaryNavigationVariant === "permanent" || isPrimaryNavigationExpanded;

  const resizeGesturesBind = useGesture(
    {
      onDrag: ({
        offset: [newWidth],
        lastOffset: [startWidth],
        cancel,
        memo,
      }) => {
        if (newWidth < PRIMARY_NAVIGATION_COLLAPSE_WIDTH) {
          cancel();
          setPrimaryNavigationWidth(startWidth);
          collapsePrimaryNavigation();
        } else {
          setPrimaryNavigationWidth(newWidth);
        }
        return memo;
      },
    },
    {
      drag: { enabled: isDrawerOpen, from: [primaryNavigationWidth, 0] },
    },
  );

  useHotkeys("[", () =>
    setPrimaryNavigationVariant(
      primaryNavigationVariant === "temporary" ? "permanent" : "temporary",
    ),
  );

  return (
    <Box
      sx={{
        width:
          primaryNavigationVariant === "temporary"
            ? "8px"
            : primaryNavigationWidth,
        height: "100%",
        transition: "width 300ms ease",
      }}
    >
      <PrimaryNavigationDrawer
        width={primaryNavigationWidth}
        topOffset={topOffset}
        variant={primaryNavigationVariant}
        open={isDrawerOpen}
        onClose={collapsePrimaryNavigation}
        transitionDuration={300}
      >
        <PrimaryNavigationContainer>{children}</PrimaryNavigationContainer>
        {isDrawerOpen && (
          <Tooltip
            title={
              <Stack>
                <Typography variant="caption">
                  <strong>Drag</strong> to Resize
                </Typography>
              </Stack>
            }
            placement="right"
            arrow
            followCursor
          >
            <ResizeTrack {...resizeGesturesBind()} />
          </Tooltip>
        )}
      </PrimaryNavigationDrawer>
    </Box>
  );
};
